import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Phone, MessageCircle, MousePointerClick, CalendarCheck, LogOut, RefreshCcw,
  Smartphone, Monitor, MapPin, Loader2, Activity, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tp-admin-pw";

const COLORS = ["#1E63FF", "#0F9D58", "#F4B400", "#DB4437", "#673AB7", "#009688", "#E91E63", "#FF6D00"];

const EVENT_ICONS = {
  call: Phone,
  whatsapp: MessageCircle,
  book_click: CalendarCheck,
  enquiry_submit: MousePointerClick,
  booking_submit: CalendarCheck,
  language_change: MousePointerClick,
  theme_change: MousePointerClick,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function AdminAnalytics() {
  const [password, setPassword] = useState(() => (typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) || "" : ""));
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Try stored password on mount
  useEffect(() => {
    if (!password) { setAuthLoading(false); return; }
    axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": password } })
      .then(() => setAuthed(true))
      .catch(() => { setPassword(""); try { window.localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ } })
      .finally(() => setAuthLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": password } });
      try { window.localStorage.setItem(STORAGE_KEY, password); } catch (_e) { /* ignore */ }
      setAuthed(true);
    } catch (err) {
      setAuthError(err?.response?.status === 401 ? "Wrong password" : "Server error — please try again");
    }
  };

  const logout = () => {
    setPassword(""); setAuthed(false); setData(null);
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  };

  const load = async (d = days) => {
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/admin/analytics?days=${d}`, { headers: { "X-Admin-Password": password } });
      setData(r.data);
    } catch (err) {
      setError("Failed to load analytics");
      if (err?.response?.status === 401) logout();
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) load(days); }, [authed, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    if (!data) return null;
    const byType = Object.fromEntries((data.by_event_type || []).map((r) => [r.key, r.count]));
    return {
      total: data.total_events,
      calls: byType.call || 0,
      whatsapp: byType.whatsapp || 0,
      bookings: data.bookings || 0,
      enquiries: data.enquiries || 0,
      bookClicks: byType.book_click || 0,
    };
  }, [data]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A0A0A] via-[#0f1729] to-[#0A0A0A] p-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-[#141414] border border-white/10 rounded-2xl p-7 shadow-[0_30px_80px_-30px_rgba(30,99,255,0.5)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#1E63FF] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider-2 text-neutral-500">TyresPro Ltd</div>
              <h1 className="text-lg font-semibold text-white">Owner Dashboard</h1>
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider-2 text-neutral-500">Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="••••••••••••"
              className="mt-2 w-full h-12 px-4 rounded-lg bg-[#0A0A0A] border border-white/10 text-white placeholder-neutral-500 focus:border-[#1E63FF] outline-none"
            />
          </label>
          {authError && (
            <div data-testid="admin-login-error" className="mt-3 text-sm text-red-400">{authError}</div>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-5 w-full h-12 rounded-full bg-[#1E63FF] text-white font-semibold hover:bg-[#1a58e0] transition-colors"
          >
            Sign in
          </button>
          <a href="/" className="mt-5 block text-center text-xs text-neutral-500 hover:text-white transition-colors">
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1E63FF] flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider-2 text-neutral-500">TyresPro</div>
              <h1 className="text-base sm:text-lg font-semibold">Analytics</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="admin-days-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(days)}
              className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 hover:border-[#1E63FF] flex items-center justify-center transition-colors"
              aria-label="Refresh"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 hover:border-red-400 text-sm inline-flex items-center gap-2 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
        {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm">{error}</div>}

        {/* Stat cards */}
        <section data-testid="admin-stat-cards" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total events" value={stats?.total ?? 0} icon={Activity} accent />
          <StatCard label="Call clicks" value={stats?.calls ?? 0} icon={Phone} />
          <StatCard label="WhatsApp clicks" value={stats?.whatsapp ?? 0} icon={MessageCircle} />
          <StatCard label="Book-now clicks" value={stats?.bookClicks ?? 0} icon={MousePointerClick} />
          <StatCard label="Bookings submitted" value={stats?.bookings ?? 0} icon={CalendarCheck} />
          <StatCard label="Enquiries submitted" value={stats?.enquiries ?? 0} icon={MousePointerClick} />
        </section>

        {/* Timeline */}
        <section className="mt-6 p-4 sm:p-6 rounded-xl bg-white/[0.03] border border-white/10">
          <h2 className="text-sm font-semibold uppercase tracking-wider-2 text-neutral-400 mb-4">Events over time</h2>
          <div className="h-56 sm:h-64">
            <ResponsiveContainer>
              <LineChart data={data?.timeline || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} labelStyle={{ color: "#fff" }} />
                <Line type="monotone" dataKey="count" stroke="#1E63FF" strokeWidth={2} dot={{ fill: "#1E63FF", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Two-col charts */}
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <ChartCard title="Top button sources">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={(data?.by_source || []).slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#a1a1aa", fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="key" tick={{ fill: "#a1a1aa", fontSize: 11 }} width={120} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#1E63FF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Event types">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data?.by_event_type || []} dataKey="count" nameKey="key" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {(data?.by_event_type || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Where visitors are from (top cities)">
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
              {(data?.by_city || []).filter((r) => r.key && r.key !== "(none)").slice(0, 15).map((r) => (
                <div key={r.key} className="flex items-center gap-3 py-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#1E63FF] shrink-0" />
                  <span className="flex-1 text-sm truncate">{r.key}</span>
                  <span className="text-xs font-bold text-white/70">{r.count}</span>
                </div>
              ))}
              {!((data?.by_city || []).filter((r) => r.key && r.key !== "(none)").length) && (
                <div className="text-xs text-neutral-500">No city data yet — geolocation kicks in as real visitors arrive.</div>
              )}
            </div>
          </ChartCard>

          <ChartCard title="Device breakdown">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data?.by_device || []} dataKey="count" nameKey="key" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {(data?.by_device || []).map((r, i) => (
                    <Cell key={r.key} fill={r.key === "mobile" ? "#1E63FF" : r.key === "desktop" ? "#0F9D58" : "#F4B400"} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </section>

        {/* Recent events */}
        <section className="mt-6 rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider-2 text-neutral-400">Latest events</h2>
            <span className="text-xs text-neutral-500">{data?.recent?.length || 0} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider-2 text-neutral-500 border-b border-white/5">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Device</th>
                  <th className="px-4 py-3">Location</th>
                </tr>
              </thead>
              <tbody data-testid="admin-recent-events">
                {(data?.recent || []).map((e) => {
                  const Icon = EVENT_ICONS[e.event_type] || MousePointerClick;
                  return (
                    <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#1E63FF]/15 text-[#5a8bff] text-xs font-semibold">
                          <Icon className="w-3 h-3" /> {e.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/80 truncate max-w-[180px]">{e.source || "-"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-400">
                          {e.device === "mobile" ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                          {e.device}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400">
                        {[e.city, e.region, e.country].filter(Boolean).join(" · ") || "-"}
                      </td>
                    </tr>
                  );
                })}
                {(data?.recent || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-neutral-500">No events yet in this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <p className="mt-6 text-xs text-neutral-600">Owner-only dashboard · TyresPro Ltd</p>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-4 sm:p-5 rounded-xl border ${accent ? "bg-[#1E63FF]/10 border-[#1E63FF]/30" : "bg-white/[0.03] border-white/10"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider-2 text-neutral-500">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="p-4 sm:p-6 rounded-xl bg-white/[0.03] border border-white/10">
      <h3 className="text-sm font-semibold uppercase tracking-wider-2 text-neutral-400 mb-3">{title}</h3>
      {children}
    </div>
  );
}
